import { useId, useState, type FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button } from '@ai-tutor/ui';
import type { FinishedBlockView } from './focusViewModel';

/**
 * "What did you just learn?" - one optional line after a block. Recalling
 * what you studied is itself practice (retrieval), so it is offered every
 * time and never required. Shown on the Focus page's break panel and in the
 * break sheet.
 */
export function BlockRecap({
  block,
  onSave,
  onDismiss,
}: {
  block: FinishedBlockView;
  onSave: (text: string) => void;
  onDismiss: () => void;
}) {
  const id = useId();
  const [text, setText] = useState('');
  if (block.recap) {
    return (
      <p className='flex items-start gap-2 rounded-lg border border-border/60 px-3 py-2 text-sm text-foreground'>
        <CheckCircle2
          className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground'
          aria-hidden='true'
        />
        <span>
          <span className='text-muted-foreground'>You noted: </span>
          {block.recap}
        </span>
      </p>
    );
  }
  if (block.recapDismissed) return null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim()) onSave(text);
  };
  return (
    <form onSubmit={submit} className='space-y-2'>
      <label htmlFor={id} className='text-sm font-medium text-foreground'>
        What did you just learn?{' '}
        <span className='font-normal text-muted-foreground'>(optional)</span>
      </label>
      {/* Input and actions on one row: the break panel has to fit a laptop
        screen without scrolling. */}
      <div className='flex items-center gap-2'>
        <input
          id={id}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder='One line is plenty'
          // Where the sheet opens to: the recap is the one thing to type, and
          // without this Modal's deferred focus lands on the close button -
          // mid-sentence, for anyone who had already started typing.
          data-autofocus
          className='h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-sm:h-11 max-sm:text-base'
        />
        {/* Outline until there is something to save: a filled, greyed-out
          button reads as broken rather than waiting. */}
        <Button
          type='submit'
          size='sm'
          className='max-sm:h-11'
          variant={text.trim() ? 'default' : 'outline'}
          disabled={!text.trim()}
        >
          Save
        </Button>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='max-sm:h-11'
          onClick={onDismiss}
        >
          Skip
        </Button>
      </div>
    </form>
  );
}
